import {
  useAppDispatch,
  useAppSelector,
  useQuery,
  useValueDebounce,
} from "features/config/hooks";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  getProductData,
  resetGetProductData,
  selectGetProductData,
} from "../slices/get-product-data.slice";
import { GetProductDataModel } from "features/stock-ordering/core/domain/get-product-data.model";
import { PreviewProductData } from ".";
import { FaRegEdit } from "react-icons/fa";
import { useParams } from "react-router-dom";
import {
  dateSetup,
  groupPropertySelector,
  moneyFormatter,
  useActionAvailability,
  useDelayedEmptyState,
} from "./stock-ordering-utils";
import { ProductParam } from "features/stock-ordering/core/stock-ordering.params";
import {
  getStockOrderProducts,
  selectGetStockOrderProducts,
} from "../slices/get-products.slice";
import {
  closeStockProcessProductEditButton,
  selectstockOrderGlobalTrigger,
  toggleStockProcessProductEditButton,
  closeStockProcess,
} from "../slices/stock-order.slice";
import { StockOrderProductInfoProps } from "./batch-product-rating-display";
import { FaPesoSign } from "react-icons/fa6";
import { AddProductSelectModal, PopupModalV2 } from "../modals";
import {
  closeModalScroll,
  toggleModalScroll,
} from "../slices/modalScrollSlice.slice";
import { StockProductItem } from "features/stock-ordering/core/domain/get-stock-product.model";
import { PopupV2ButtonConfigs } from "../modals/popup-v2.modal";
import {
  resetstockOrderedEditProductStatus,
  selectStockOrderedEditProduct,
  stockOrderedEditProduct,
  StockOrderedEditProductState,
} from "../slices/stock-order-edit-product.slice";
import {
  BatchProductReviewsState,
  getBatchProductReviews,
  selectBatchProductReviews,
} from "../slices/batch-product-reviews.slice";
import {
  tableDataChanged,
  tableDataSave,
} from "../slices/tableDataChange.slice";
import { IoCloudDoneOutline } from "react-icons/io5";
import { Calendar, DollarSign, Package, Truck } from "lucide-react";
import { TextField } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  closeMessageModal,
  openMessageModal,
} from "features/shared/presentation/slices/message-modal.slice";
import { staggeredReceive } from "../slices/staggered-receive.slice";
import { staggeredDispatch } from "../slices/staggered-dispatch.slice";
import { REACT_APP_DOMAIN_URL } from "features/shared/constants";

export interface TableDataChange {
  index: number;
  quantity: number;
  subTotal: number;
  out_of_stock?: boolean | null;
}

export function StockOrderProductInfo({
  onRatingClick,
}: StockOrderProductInfoProps = {}) {
  /**
   * Fetch queries
   * @global orderId - id of the order
   * @global tab     - tab value or current process
   */
  const query = useQuery();
  const tab = query.get("tab");

  /**
   * url param order/selling
   */
  const { view, orderId } = useParams();

  const dispatch = useAppDispatch();

  /**
   * Product data selector
   * adminsession selector
   */
  const getProductDataState = useAppSelector(selectGetProductData);
  const getProductInformation = useAppSelector(selectGetStockOrderProducts);
  const globalTriggerState = useAppSelector(selectstockOrderGlobalTrigger);
  const orderedEditItemState = useAppSelector(selectStockOrderedEditProduct);
  const getBatchProductReviewsState = useAppSelector(selectBatchProductReviews);

  /**
   * Destructure order information
   */
  const { order_information, product_data, product_batch_data } =
    getProductDataState.data ?? {
      order_information: {
        category_id: null,
        store_id: null,
        store_name: null,
        companyId: undefined,
        supplierId: null,
        managerApproval: false,
        status_id: null,
        nextDelivery: null,
      },
    };

  /**
   * Destructure process edit button
   */
  const { processEditProductButton } = globalTriggerState;

  /**
   * states
   */
  const [orderProductData, setOrderProductData] = useState<
    GetProductDataModel["product_data"]
  >([]);

  const [selectedProductData, setSelectedProductData] =
    useState<StockProductItem[]>();

  const [batchDeliveryDates, setBatchDeliveryDates] = useState<{
    [key: string]: string;
  }>({});

  const [removedProductIds, setRemovedProductIds] = useState<string[]>([]);

  const [openSaveChanges, setOpenSaveChanges] = useState(false);

  const [openProductsModal, setOpenProductsModal] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");

  const { processId } = useActionAvailability();

  const managerPerm = useActionAvailability(3);
  // const procurementPerm = useActionAvailability(2);

  const { showEmpty, isWaiting } = useDelayedEmptyState(
    orderProductData.length !== 0,
    30,
  );

  //debounce qty change
  const [activeProductDataChange, activeStatus] = useValueDebounce(
    orderProductData,
    3000,
  );

  useEffect(() => {
    if (!activeStatus) {
      dispatch(tableDataSave());

      return;
    }

    dispatch(
      tableDataChanged({
        data: {
          totalCost,
          productData: activeProductDataChange,
          isSaved: activeStatus,
        },
      }),
    );
  }, [dispatch, activeProductDataChange, activeStatus]);

  // Batch Rating fetch
  useEffect(() => {
    if (orderProductData.length > 0) {
      if (
        orderProductData.length > 0 &&
        BatchProductReviewsState?.success !== getBatchProductReviewsState.status
      ) {
        const productIds = orderProductData.map(
          (product) => product.product_id,
        );
        dispatch(getBatchProductReviews(productIds));
      }
    }
  }, [orderProductData, dispatch]);

  /**
   * Refetch if product data is empty
   * @param getProductDataState - from redux store
   * @param orderId - from endpoint parameter
   */

  useEffect(() => {
    // Fetch data if not available
    if (!getProductDataState.data && orderId !== null) {
      dispatch(getProductData({ orderId }));
      return;
    }

    // Preload product data if conditions are met
    const isStatus4 = order_information.status_id === 4;
    const isStatus5 = order_information.status_id === 5;

    if (
      orderProductData.length === 0 &&
      product_data &&
      order_information.status_id
    ) {
      if (
        isStatus4 &&
        product_data.some((prod) => prod.dispatch_qty === null)
      ) {
        const updatedProducts = product_data.map((product) => ({
          ...product,
          dispatch_qty: (product.commited_qty ?? product.order_qty) || 0,
          dispatch_subtotal:
            product.total_cost ?? product.productCost * product.order_qty,
        }));
        setOrderProductData(updatedProducts);
      } else if (
        isStatus5 &&
        product_data.some((prod) => prod.delivered_qty === null)
      ) {
        const updatedProducts = product_data.map((product) => ({
          ...product,
          delivered_qty: (product.dispatch_qty ?? product.order_qty) || 0,
          receive_subtotal:
            product.total_cost ?? product.productCost * product.order_qty,
        }));
        setOrderProductData(updatedProducts);
      } else {
        setOrderProductData(product_data);
      }
    }
  }, [getProductDataState.data]);

  //Reset refetch
  useEffect(() => {
    if (orderedEditItemState.status === StockOrderedEditProductState.success) {
      setSelectedProductData([]);

      dispatch(resetGetProductData());
      dispatch(getProductData({ orderId: orderId }));

      setOrderProductData([]);
      dispatch(resetstockOrderedEditProductStatus());
    }
  }, [orderedEditItemState.status]);

  useEffect(() => {
    if (getProductInformation.data?.products.length === 0) {
      getProductsInformation();
    }
  }, [getProductInformation]);

  /**
   *  Call back to fetch all products products data
   */
  const getProductsInformation = useCallback(() => {
    const productParams: ProductParam = {
      category: order_information?.category_id,
      store_information: {
        store_id: order_information.store_id,
        store_name: order_information.store_name,
      },
      company_id: order_information.companyId,
      supplier: order_information.supplierId,
    };

    dispatch(getStockOrderProducts(productParams));
    dispatch(toggleModalScroll());
    setOpenProductsModal(true);
  }, [openProductsModal, dispatch]);

  /**
   * Handle product data change
   * @param data - passed as a prop
   */

  const dispatcherAccess = useActionAvailability(6).permission;

  const stockClerkAccess = useActionAvailability(1).permission;
  const supplierAccess = useActionAvailability(5).permission;

  const actionAvailableAccess = useActionAvailability().actionAvailable;

  const handleProductDataChange = (data: TableDataChange) => {
    setOrderProductData((prev) =>
      prev.map((product, index) => {
        if (index === data.index) {
          // if (processEditProductButton) {
          //   return {
          //     ...product,
          //     order_qty: data.quantity,
          //     orderSubTotalAfter: data.subTotal,
          //   };
          // } else {
          if (processId === 4 && actionAvailableAccess && dispatcherAccess) {
            return {
              ...product,
              dispatch_qty: data.quantity,
              dispatch_subtotal: data.subTotal,
            };
          } else if (
            processId === 5 &&
            actionAvailableAccess &&
            stockClerkAccess
          ) {
            return {
              ...product,
              delivered_qty: data.quantity,
              receive_subtotal: data.subTotal,
            };
          }
          // }
        }
        return product;
      }),
    );
  };

  const handleBatchReceive = (batchHash: string) => () => {
    dispatch(
      openMessageModal({
        useHtml: true,
        message: `
           Are you sure you want to receive? By clicking <strong>Yes</strong> you agree to the 
          <a href="terms-and-condition" 
            target="_blank" 
            rel="noopener noreferrer" 
            style="color: #2563eb; text-decoration: underline;">
            Terms and Conditions
          </a>.
        `,
        buttons: [
          {
            color: "#22201A",
            text: "No",
            onClick: () => {
              dispatch(closeMessageModal());
            },
          },
          {
            color: "#CC5801",
            text: "I confirm",
            onClick: () => {
              dispatch(
                staggeredReceive({
                  orderId: orderId,
                  payload: {
                    batchHash: batchHash,
                  },
                }),
              );
              dispatch(closeStockProcess());
              dispatch(closeMessageModal());
            },
          },
        ],
      }),
    );
  };

  const handleBatchDispatch = (batchHash: string) => () => {
    dispatch(
      openMessageModal({
        useHtml: true,
        message: `Are you sure you want to dispatch? By clicking <strong>Yes</strong> you agree to the 
          <a href="terms-and-condition" 
            target="_blank" 
            rel="noopener noreferrer" 
            style="color: #2563eb; text-decoration: underline;">
            Terms and Conditions
          </a>`,
        buttons: [
          {
            color: "#22201A",
            text: "No",
            onClick: () => {
              dispatch(closeMessageModal());
            },
          },
          {
            color: "#CC5801",
            text: "I confirm",
            onClick: () => {
              dispatch(
                staggeredDispatch({
                  orderId: orderId,
                  payload: {
                    batchHash: batchHash,
                  },
                }),
              );
              dispatch(closeStockProcess());
              dispatch(closeMessageModal());
            },
          },
        ],
      }),
    );
  };

  /**
   * computes total cost
   */

  const totalCost = orderProductData.reduce((acc, product) => {
    // if (processEditProductButton) {
    //   const computedOrder = acc + Number(product.orderSubTotalAfter);
    //   return computedOrder + 0;
    // } else

    if (processId === 4) {
      return acc + Number(product.dispatch_subtotal);
    } else if (processId === 5) {
      return acc + Number(product.receive_subtotal);
    } else {
      return acc + Number(product.total_cost);
    }
  }, 0);

  /**
   * Compute Pending Changes total cost
   */
  const stagedTotalCost = selectedProductData?.reduce(
    (acc, product) => acc + product.orderQty * product.cost,
    0,
  );

  const removedProductsCost = orderProductData
    .filter((product) => removedProductIds.includes(product.product_id))
    .reduce((acc, product) => {
      // if (processEditProductButton) {
      //   return acc + Number(product.orderSubTotalAfter);
      // } else

      if (processId === 4) {
        return acc + Number(product.dispatch_subtotal);
      } else if (processId === 5) {
        return acc + Number(product.receive_subtotal);
      } else {
        return acc + Number(product.total_cost);
      }
    }, 0);

  const finalTotal = (stagedTotalCost || 0) + totalCost - removedProductsCost;

  // Enable Edit Product
  const editMode = () => {
    // setIsEdit(!isEdit);
    dispatch(toggleStockProcessProductEditButton());
    setSearchQuery("");
  };

  const handleCancelEdit = () => {
    dispatch(closeModalScroll());
    setOpenSaveChanges(false);
    setBatchDeliveryDates({});
    setRemovedProductIds([]);
    setSelectedProductData([]);
    dispatch(closeStockProcessProductEditButton());
    setSearchQuery("");
  };

  const handleOpenSaveChangesModal = () => {
    dispatch(toggleModalScroll());
    setOpenSaveChanges(true);
  };

  const handleSaveChanges = () => {
    if (orderId) {
      dispatch(
        stockOrderedEditProduct({
          orderId: Number(orderId),
          data: {
            AddedProductItems: selectedProductData ?? [],
            removedProducts: removedProductIds ?? [],
            batchDeliveryDates: batchDeliveryDates ?? undefined,
          },
        }),
      );

      // cleaning
      setOpenSaveChanges(false);
      setRemovedProductIds([]);
      setSelectedProductData([]);
      setBatchDeliveryDates({});
      dispatch(closeModalScroll());
      dispatch(closeStockProcessProductEditButton());
      setSearchQuery("");
    }
  };

  const handleToggleRemoveProduct = (productId: string, index: number) => {
    setRemovedProductIds((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden font-[Roboto]">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Order Product Information
                </h2>
                <div className="flex space-x-2">
                  <p className="text-sm text-gray-600">
                    {orderProductData.length} item
                    {orderProductData.length !== 1 ? "s" : ""} in this order
                  </p>

                  <p className="text-sm text-gray-600">
                    &bull;{" "}
                    {activeStatus ? (
                      <IoCloudDoneOutline className="ml-1 inline-block" />
                    ) : (
                      " Saving..."
                    )}
                  </p>
                </div>
              </div>
            </div>

            {managerPerm.permission &&
              order_information.status_id === 1 &&
              !processEditProductButton && (
                <button
                  onClick={editMode}
                  className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
                >
                  <FaRegEdit />
                  <span className="hidden md:block text-sm font-medium">
                    {product_batch_data?.length !== 0 &&
                    order_information.status_id === 1
                      ? "Setup batch delivery"
                      : "Edit"}
                  </span>
                </button>
              )}
          </div>

          <div className="relative mt-3">
            {!processEditProductButton && (
              <div className="relative mt-3">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder={
                    product_batch_data?.length === 0
                      ? "Search products..."
                      : "Search batch..."
                  }
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                />
              </div>
            )}
          </div>
        </div>

        {/* Products List */}
        <div className="p-6 max-h-96 w-full overflow-auto">
          {order_information?.status_id && orderProductData.length > 0 ? (
            <div className="space-y-4">
              {(() => {
                const filteredProducts = orderProductData.filter((prod) =>
                  prod.product_name
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()),
                );

                const filterBatch = product_batch_data?.filter((batch) =>
                  batch.batch_name
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()),
                );

                if (product_batch_data?.length === 0) {
                  if (filteredProducts.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center py-6 text-gray-500">
                        No products found
                      </div>
                    );
                  }

                  return filteredProducts.map((productData, index) => (
                    <div
                      key={`${productData.id}-${index}`}
                      className="relative"
                    >
                      <PreviewProductData
                        idx={index}
                        productData={productData}
                        setProductData={handleProductDataChange}
                        statusId={order_information?.status_id}
                        onRatingClick={onRatingClick}
                        onToggleRemoveProduct={handleToggleRemoveProduct}
                        isRemoved={removedProductIds.includes(
                          productData.product_id,
                        )}
                      />
                    </div>
                  ));
                } else {
                  if (filterBatch?.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center py-6 text-gray-500">
                        No Batch found
                      </div>
                    );
                  }

                  const handleBatchDateChange = (
                    batchHash: string,
                    newDate: string,
                  ) => {
                    setBatchDeliveryDates((prevDates) => ({
                      ...prevDates,
                      [batchHash]: newDate,
                    }));
                  };

                  const editDisableDates =
                    (batch_hash: string) =>
                    (
                      day:
                        | string
                        | number
                        | Date
                        | dayjs.Dayjs
                        | null
                        | undefined,
                    ) => {
                      const isInProductBatches = !!product_batch_data?.some(
                        (fnd) =>
                          // only disable if the batch is different
                          fnd.batch_hash !== batch_hash &&
                          dayjs(fnd.delivery_date).isSame(day, "day"),
                      );

                      const isInEditedBatches = !!Object.entries(
                        batchDeliveryDates,
                      ).some(
                        ([key, date]) =>
                          // only disable if it's another batch, not the one currently being edited
                          key !== batch_hash && dayjs(date).isSame(day, "day"),
                      );

                      return isInProductBatches || isInEditedBatches;
                    };

                  return (
                    <div className="space-y-6">
                      {filterBatch?.map((batch, batchIndex) => (
                        <div
                          key={batch.batch_hash || batchIndex}
                          className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
                        >
                          <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-4 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                  <Package className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                  <h3 className="font-semibold text-gray-900 text-lg">
                                    {batch.batch_name}
                                  </h3>
                                  <p className="text-sm text-gray-500">
                                    {
                                      orderProductData.filter(
                                        (p) =>
                                          p.batch_hash === batch.batch_hash,
                                      ).length
                                    }{" "}
                                    items
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-6">
                                <div className="text-right">
                                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                                    <Calendar className="h-4 w-4" />
                                    <span>Delivery Date</span>
                                  </div>

                                  {processEditProductButton &&
                                  supplierAccess ? (
                                    <LocalizationProvider
                                      dateAdapter={AdapterDayjs}
                                    >
                                      <DatePicker
                                        value={
                                          processEditProductButton
                                            ? batchDeliveryDates[
                                                batch.batch_hash
                                              ]
                                              ? dayjs(
                                                  batchDeliveryDates[
                                                    batch.batch_hash
                                                  ],
                                                )
                                              : dayjs(batch.delivery_date)
                                            : dayjs(batch.delivery_date)
                                        }
                                        onChange={(newValue) => {
                                          if (newValue) {
                                            handleBatchDateChange(
                                              batch.batch_hash,
                                              newValue.format("YYYY-MM-DD"),
                                            );
                                          }
                                        }}
                                        minDate={dayjs()}
                                        shouldDisableDate={editDisableDates(
                                          batch.batch_hash,
                                        )}
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            size="small"
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                fontSize: "0.875rem",
                                                fontWeight: 600,
                                                color: "#111827",
                                                backgroundColor: "#ffffff",
                                                borderRadius: "0.5rem",
                                                "& fieldset": {
                                                  borderColor: "#d1d5db",
                                                },
                                                "&:hover fieldset": {
                                                  borderColor: "#9ca3af",
                                                },
                                                "&.Mui-focused fieldset": {
                                                  borderColor: "#3b82f6",
                                                  borderWidth: "2px",
                                                },
                                              },
                                              "& .MuiOutlinedInput-input": {
                                                padding: "8px 12px",
                                              },
                                            }}
                                          />
                                        )}
                                      />
                                    </LocalizationProvider>
                                  ) : batch.delivery_date ? (
                                    <p className="font-semibold text-gray-900">
                                      {new Date(
                                        batch.delivery_date,
                                      ).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                        day: "numeric",
                                      })}
                                    </p>
                                  ) : (
                                    "TBD"
                                  )}
                                </div>

                                <div className="text-right">
                                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                                    <FaPesoSign className="h-4 w-4" />
                                    <span>Total Cost</span>
                                  </div>
                                  <p className="font-bold text-xl text-blue-600">
                                    ₱{batch.total_cost?.toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="p-6">
                            {orderProductData
                              .filter(
                                (fltr) => fltr.batch_hash === batch.batch_hash,
                              )
                              .map((productData, index) => (
                                <div
                                  key={productData.product_id || index}
                                  className="relative"
                                >
                                  <PreviewProductData
                                    idx={index}
                                    productData={productData}
                                    setProductData={handleProductDataChange}
                                    statusId={order_information?.status_id}
                                    onRatingClick={onRatingClick}
                                    overrideHandleQuantity={
                                      !(
                                        processId === 5 &&
                                        !Boolean(batch.is_delivered) &&
                                        view === "order" &&
                                        dayjs(batch.delivery_date).isSame(
                                          dayjs(order_information.nextDelivery),
                                        )
                                      )
                                    }
                                  />
                                </div>
                              ))}

                            {/* Empty State */}
                            {orderProductData.filter(
                              (p) => p.batch_hash === batch.batch_hash,
                            ).length === 0 && (
                              <div className="text-center py-12 text-gray-400">
                                <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                <p className="text-gray-500">
                                  No products in this batch
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Batch Footer Summary */}
                          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4 text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                  {processId === 4 &&
                                    !Boolean(batch.is_delivered) &&
                                    view === "selling" &&
                                    dayjs(batch.delivery_date).isSame(
                                      dayjs(order_information.nextDelivery),
                                    ) && (
                                      <button
                                        onClick={handleBatchDispatch(
                                          batch.batch_hash,
                                        )}
                                        className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium 
                                    hover:bg-blue-700 active:bg-blue-800 transition-colors duration-200
                                    shadow-sm"
                                      >
                                        Dispatch
                                      </button>
                                    )}

                                  {processId === 5 &&
                                    !Boolean(batch.is_delivered) &&
                                    view === "order" &&
                                    dayjs(batch.delivery_date).isSame(
                                      dayjs(order_information.nextDelivery),
                                    ) && (
                                      <button
                                        onClick={handleBatchReceive(
                                          batch.batch_hash,
                                        )}
                                        className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium 
                                    hover:bg-blue-700 active:bg-blue-800 transition-colors duration-200
                                    shadow-sm"
                                      >
                                        Receive
                                      </button>
                                    )}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Package className="h-4 w-4" />
                                  {
                                    orderProductData.filter(
                                      (p) => p.batch_hash === batch.batch_hash,
                                    ).length
                                  }{" "}
                                  Products
                                </span>
                                <span className="flex items-center gap-1">
                                  <Truck className="h-4 w-4" />
                                  Delivery:{" "}
                                  {dateSetup(batch.delivery_date, false)}
                                </span>
                              </div>

                              {/* Batch Status Indicator */}

                              {Boolean(batch.is_delivered) ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                  <span className="text-sm font-medium text-green-700">
                                    Delivered
                                  </span>
                                </div>
                              ) : Boolean(batch.is_dispatched) ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-violet-500 rounded-full animate-pulse"></div>
                                  <span className="text-sm font-medium text-violet-700">
                                    Dispatched
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                                  <span className="text-sm font-medium text-blue-700">
                                    Active
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* No Batches State */}
                      {(!product_batch_data ||
                        product_batch_data.length === 0) && (
                        <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-300">
                          <Package className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">
                            No Batches Found
                          </h3>
                          <p className="text-gray-500">
                            There are no batch orders to display at this time.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                }
              })()}

              {selectedProductData && selectedProductData?.length > 0 && (
                <>
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                        Staged Products ({selectedProductData.length} items)
                      </h3>

                      <button
                        onClick={() => setSelectedProductData([])}
                        className="text-xs bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700 px-2 py-1 rounded-md border border-red-200 hover:border-red-300 transition-all duration-200 flex items-center gap-1"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H9a1 1 0 00-1 1v1M4 7h16"
                          />
                        </svg>
                        Clear All
                      </button>
                    </div>
                    <div className="space-y-4">
                      {selectedProductData?.map((prod, index) => (
                        <div key={prod.product_id} className="relative">
                          <PreviewProductData
                            idx={index}
                            productData={prod}
                            setProductData={handleProductDataChange}
                            statusId={order_information?.status_id}
                            onRatingClick={onRatingClick}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {processEditProductButton && product_batch_data?.length === 0 && (
                <AddProductSection
                  getProductsInformation={getProductsInformation}
                />
              )}
            </div>
          ) : (
            <>
              {isWaiting && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Loading Products...
                  </h3>
                  <p className="text-sm text-gray-500">
                    Please wait while we fetch your data
                  </p>
                </div>
              )}

              {showEmpty && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-2M4 13h2m0 0V9a2 2 0 012-2h2m0 0V6a2 2 0 012-2h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 01.293.707V9"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Products Available
                  </h3>
                </div>
              )}
            </>
          )}
        </div>

        {/* Total Cost Footer */}
        {orderProductData.length > 0 && (
          <div className="bg-white border-t border-gray-200">
            {/* Edit Mode Action Bar */}
            {processEditProductButton && (
              <div className="bg-gray-50 border-b border-gray-200 px-4 sm:px-6 py-3">
                <div className="flex items-center justify-between">
                  {/* Enhanced warning with specific change details */}
                  {((selectedProductData && selectedProductData?.length > 0) ||
                    removedProductIds.length > 0) && (
                    <div className="flex items-center space-x-2 animate-pulse">
                      <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-amber-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                          />
                        </svg>
                      </div>

                      <span className="text-sm text-gray-600">
                        {(() => {
                          const stagedCount = selectedProductData?.length || 0;
                          const removedCount = removedProductIds.length;

                          if (stagedCount > 0 && removedCount > 0) {
                            return `You have ${stagedCount} staged and ${removedCount} removed items`;
                          } else if (stagedCount > 0) {
                            return `You have ${stagedCount} staged item${
                              stagedCount > 1 ? "s" : ""
                            }`;
                          } else if (removedCount > 0) {
                            return `You have ${removedCount} removed item${
                              removedCount > 1 ? "s" : ""
                            }`;
                          }
                          return "You have unsaved changes";
                        })()}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleCancelEdit}
                      className="text-sm text-gray-600 hover:text-gray-800 font-medium transition-colors duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      disabled={
                        (!selectedProductData ||
                          selectedProductData.length === 0) &&
                        removedProductIds.length === 0 &&
                        (!product_batch_data || product_batch_data.length === 0)
                      }
                      onClick={handleOpenSaveChangesModal}
                      className={`
                        hidden lg:block text-sm font-medium px-4 py-2 rounded-lg transition-all duration-200 shadow-sm
                        ${
                          (!selectedProductData ||
                            selectedProductData.length === 0) &&
                          removedProductIds.length === 0 &&
                          (!product_batch_data ||
                            product_batch_data.length === 0)
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
                            : "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-md hover:scale-105 active:scale-95"
                        }
                      `}
                    >
                      Save Changes
                    </button>

                    <button
                      disabled={
                        (!selectedProductData ||
                          selectedProductData?.length === 0) &&
                        removedProductIds.length === 0
                      }
                      onClick={handleOpenSaveChangesModal}
                      className={`
                         lg:hidden text-sm font-medium px-4 py-2 rounded-lg transition-all duration-200 shadow-sm
                        ${
                          (!selectedProductData ||
                            selectedProductData?.length === 0) &&
                          removedProductIds.length === 0
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
                            : "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-md hover:scale-105 active:scale-95"
                        }
                      `}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white border-t border-gray-200 p-4 sm:p-6">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 sm:p-6">
                {/* Mobile Layout */}
                <div className="block sm:hidden">
                  <div className="text-center space-y-3">
                    {/* Icon and Title */}
                    <div className="flex items-center justify-center space-x-3">
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                        <FaPesoSign className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Order Total
                      </h3>
                    </div>

                    {/* Amount */}
                    <div className="text-3xl font-bold text-gray-900">
                      ₱{moneyFormatter(finalTotal, 2)}
                    </div>

                    {/* Details */}
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">
                        {selectedProductData && selectedProductData?.length > 0
                          ? `${selectedProductData.length} staged + ${orderProductData.length} current`
                          : `${orderProductData.length} current items`}
                        {removedProductIds.length > 0 && (
                          <span className="text-red-600">
                            {" "}
                            - {removedProductIds.length} removed
                          </span>
                        )}
                      </p>

                      {/* Breakdown */}
                      <div className="text-xs text-gray-500 space-y-1">
                        {selectedProductData &&
                          selectedProductData?.length > 0 && (
                            <div className="flex justify-between">
                              <span>Staged:</span>
                              <span className="text-green-600">
                                +₱{moneyFormatter(stagedTotalCost, 2)}
                              </span>
                            </div>
                          )}
                        <div className="flex justify-between">
                          <span>Current:</span>
                          <span className="text-blue-600">
                            ₱{moneyFormatter(totalCost, 2)}
                          </span>
                        </div>
                        {removedProductIds.length > 0 && (
                          <div className="flex justify-between">
                            <span>Removed:</span>
                            <span className="text-red-600">
                              -₱{moneyFormatter(removedProductsCost, 2)}
                            </span>
                          </div>
                        )}
                        <div className="border-t border-gray-300 pt-1 mt-2">
                          <div className="flex justify-between font-semibold">
                            <span>Final Total:</span>
                            <span>₱{moneyFormatter(finalTotal, 2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden sm:flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                      <FaPesoSign className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Order Total
                      </h3>
                      <p className="text-sm text-gray-600">
                        {selectedProductData && selectedProductData?.length > 0
                          ? `${selectedProductData.length} staged + ${orderProductData.length} current`
                          : `${orderProductData.length} current items`}
                        {removedProductIds.length > 0 && (
                          <span className="text-red-600">
                            {" "}
                            - {removedProductIds.length} removed
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-3xl font-bold text-gray-900">
                      ₱{moneyFormatter(finalTotal, 2)}
                    </div>

                    {/* Breakdown for desktop */}
                    <div className="text-sm text-gray-500 space-y-1 mt-2">
                      <div className="flex items-center gap-2">
                        {selectedProductData &&
                          selectedProductData?.length > 0 && (
                            <>
                              <span className="text-green-600">
                                Staged: ₱{moneyFormatter(stagedTotalCost, 2)}
                              </span>
                              <span>•</span>
                            </>
                          )}
                        <span className="text-blue-600">
                          Current: ₱{moneyFormatter(totalCost, 2)}
                        </span>
                        {removedProductIds.length > 0 && (
                          <>
                            <span>•</span>
                            <span className="text-red-600">
                              Removed: -₱
                              {moneyFormatter(removedProductsCost, 2)}
                            </span>
                          </>
                        )}
                      </div>

                      {/* Show calculation if there are multiple components */}
                      {((selectedProductData &&
                        selectedProductData?.length > 0) ||
                        removedProductIds.length > 0) && (
                        <div className="text-xs text-gray-400 border-t border-gray-200 pt-1">
                          Final:{" "}
                          {selectedProductData &&
                          selectedProductData?.length > 0
                            ? `₱${moneyFormatter(stagedTotalCost, 2)} + `
                            : ""}
                          ₱{moneyFormatter(totalCost, 2)}
                          {removedProductIds.length > 0
                            ? ` - ₱${moneyFormatter(removedProductsCost, 2)}`
                            : ""}{" "}
                          = ₱{moneyFormatter(finalTotal, 2)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Removed Products Alert (if any) */}
                {removedProductIds.length > 0 && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-bold">!</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-red-700 font-medium">
                          {removedProductIds.length} product
                          {removedProductIds.length > 1 ? "s" : ""} marked for
                          removal
                        </span>
                        <span className="text-red-600 ml-2">
                          (-₱{moneyFormatter(removedProductsCost, 2)})
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <AddProductSelectModal
        isOpen={openProductsModal}
        onClose={() => {
          setOpenProductsModal(false);
          dispatch(toggleModalScroll());
        }}
        onConfirm={(data) => setSelectedProductData(data)}
        products={
          getProductInformation.data?.products?.filter(
            (p) =>
              !new Set(orderProductData?.map((o) => String(o.product_id))).has(
                String(p.product_id),
              ),
          ) ?? []
        }
        preSelectedProducts={selectedProductData}
        multiSelect
        withQuantity
      />

      <PopupModalV2
        open={openSaveChanges}
        onClose={() => {
          setOpenSaveChanges(false);
          dispatch(closeModalScroll());
        }}
        theme="ios"
        buttons={PopupV2ButtonConfigs.saveChanges(
          handleSaveChanges,
          handleCancelEdit,
          () => {
            setOpenSaveChanges(false);
            dispatch(closeModalScroll());
          },
        )}
      />
    </>
  );
}

const AddProductSection = memo(
  ({ getProductsInformation }: { getProductsInformation: () => void }) => {
    return (
      <button
        onClick={getProductsInformation}
        className="w-full border-2 border-dashed border-gray-200 rounded-lg p-3 text-center hover:border-blue-300 hover:bg-blue-50/30 transition-colors duration-200 cursor-pointer group"
      >
        <div className="flex flex-col items-center space-y-1.5">
          <div className="w-8 h-8 bg-gray-100 group-hover:bg-blue-100 rounded-full flex items-center justify-center transition-colors duration-200">
            <svg
              className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors duration-200"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-xs font-medium text-gray-700 group-hover:text-blue-700 transition-colors duration-200">
              Add Another Product
            </h3>
            <p className="text-[10px] text-gray-500">Click to add more items</p>
          </div>
        </div>
      </button>
    );
  },
);
